import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { useImmerReducer } from 'use-immer';
import { initFullscreenOverlay, closeFullscreenOverlay } from '../../utils/fullscreen-overlay';
import { disableAllUITabIndex, restoreAllDisabledTabIndex, restoreAllUIAriaHidden, setAllUIAriaHidden } from '../../utils';
import { usePrevious } from '../../hooks';
import { ROOT_EL_ID } from '../../constants';
var ModalContextActions;

(function (ModalContextActions) {
  ModalContextActions["OPEN"] = "OPEN";
  ModalContextActions["CLOSE"] = "CLOSE";
  ModalContextActions["CLEANUP"] = "CLEANUP";
  ModalContextActions["SET_ROOT_EL"] = "SET_ROOT_EL";
})(ModalContextActions || (ModalContextActions = {}));

var initialState = {
  isOpen: false,
  modalId: '',
  focusReturnEl: null,
  rootEl: null,
  multipleModalCache: []
};

var reducer = function reducer(draft, action) {
  switch (action.type) {
    case ModalContextActions.SET_ROOT_EL:
      {
        var payload = action.payload;
        draft.rootEl = payload;
        break;
      }

    case ModalContextActions.OPEN:
      {
        var _action$payload = action.payload,
            focusReturnEl = _action$payload.focusReturnEl,
            modalId = _action$payload.modalId;

        if (draft.isOpen && !draft.multipleModalCache.find(function (_ref) {
          var mId = _ref.modalId;
          return mId === modalId;
        })) {
          draft.multipleModalCache.push({
            modalId: draft.modalId,
            focusReturnEl: draft.focusReturnEl
          });
        }

        initFullscreenOverlay(modalId);
        disableAllUITabIndex(modalId);
        setAllUIAriaHidden(modalId);
        draft.isOpen = true;
        draft.modalId = modalId;
        draft.focusReturnEl = focusReturnEl;
        break;
      }

    case ModalContextActions.CLOSE:
      {
        var _action$payload2 = action.payload,
            _focusReturnEl = _action$payload2.focusReturnEl,
            _modalId = _action$payload2.modalId;
        closeFullscreenOverlay(_modalId);
        restoreAllDisabledTabIndex(_modalId);
        restoreAllUIAriaHidden(_modalId);

        if (_modalId !== draft.modalId) {
          draft.multipleModalCache = draft.multipleModalCache.filter(function (_ref2) {
            var mId = _ref2.modalId;
            return mId !== _modalId;
          });
          return;
        }

        if (draft.multipleModalCache.length !== 0) {
          var modal = draft.multipleModalCache.pop();

          if (modal) {
            var _modalId2 = modal.modalId,
                _focusReturnEl2 = modal.focusReturnEl;
            draft.modalId = _modalId2;
            draft.focusReturnEl = _focusReturnEl2;
            return;
          }
        }

        if (_focusReturnEl) {
          _focusReturnEl === null || _focusReturnEl === void 0 ? void 0 : _focusReturnEl.focus();
          draft.focusReturnEl = null;
        } else if (draft.focusReturnEl) {
          var _draft$focusReturnEl;

          (_draft$focusReturnEl = draft.focusReturnEl) === null || _draft$focusReturnEl === void 0 ? void 0 : _draft$focusReturnEl.focus();
          draft.focusReturnEl = null;
        }

        draft.isOpen = false;
        break;
      }

    case ModalContextActions.CLEANUP:
      {
        var _modalId3 = action.payload.modalId;
        closeFullscreenOverlay(_modalId3);
        restoreAllDisabledTabIndex(_modalId3);
        restoreAllUIAriaHidden(_modalId3);
        if (draft.modalId === _modalId3) draft.modalId = '';
        draft.multipleModalCache = draft.multipleModalCache.filter(function (_ref3) {
          var mId = _ref3.modalId;
          return mId !== _modalId3;
        });
        if (!draft.modalId && draft.multipleModalCache.length === 0) draft.isOpen = false;
        break;
      }

    default:
      break;
  }
};

var DispatchContext = /*#__PURE__*/createContext(undefined);
var StateContext = /*#__PURE__*/createContext(undefined);

var ModalContextProvider = function ModalContextProvider(_ref4) {
  var children = _ref4.children;

  var _useImmerReducer = useImmerReducer(reducer, initialState),
      _useImmerReducer2 = _slicedToArray(_useImmerReducer, 2),
      state = _useImmerReducer2[0],
      dispatch = _useImmerReducer2[1];

  useEffect(function () {
    dispatch({
      type: ModalContextActions.SET_ROOT_EL,
      payload: document.getElementById(ROOT_EL_ID)
    });
  }, [dispatch]);
  return /*#__PURE__*/React.createElement(StateContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(DispatchContext.Provider, {
    value: dispatch
  }, children));
};

var useModalDispatch = function useModalDispatch() {
  var dispatch = useContext(DispatchContext);

  if (dispatch === undefined) {
    throw new Error('useModalDispatch must be used within a ModalContextProvider');
  }

  return dispatch;
};

var useModalState = function useModalState() {
  var state = useContext(StateContext);

  if (state === undefined) {
    throw new Error('useModalState must be used within a ModalContextProvider');
  }

  return state;
};

var useModal = function useModal(modalId) {
  var state = useContext(StateContext);
  var dispatch = useContext(DispatchContext);

  if (state === undefined || dispatch === undefined) {
    throw new Error('useModal must be used within a ModalContextProvider');
  }

  var isOpen = state.isOpen,
      currentModalId = state.modalId,
      rootEl = state.rootEl,
      multipleModalCache = state.multipleModalCache;
  var otherModalWasOpen = usePrevious(isOpen);
  var openModal = useCallback(function (focusReturnElementIdOrRef) {
    var focusReturnEl;

    if (typeof focusReturnElementIdOrRef === 'string') {
      focusReturnEl = document.getElementById(focusReturnElementIdOrRef);
    } else if (focusReturnElementIdOrRef.target) {
      focusReturnEl = document.activeElement;
    } else {
      focusReturnEl = focusReturnElementIdOrRef;
    }

    dispatch({
      type: ModalContextActions.OPEN,
      payload: {
        focusReturnEl: focusReturnEl || null,
        modalId: modalId
      }
    });
  }, [dispatch, modalId]);
  var closeModal = useCallback(function (focusReturnElementIdOrRef) {
    var focusReturnEl = null;

    if (focusReturnElementIdOrRef) {
      if (typeof focusReturnElementIdOrRef === 'string') {
        focusReturnEl = document.getElementById(focusReturnElementIdOrRef);
      } else if (focusReturnElementIdOrRef.target) {
        focusReturnEl = null;
      } else {
        focusReturnEl = focusReturnElementIdOrRef;
      }
    }

    dispatch({
      type: ModalContextActions.CLOSE,
      payload: {
        focusReturnEl: focusReturnEl || null,
        modalId: modalId
      }
    });
  }, [dispatch, modalId]);
  return {
    otherModalWasOpen: otherModalWasOpen && isOpen,
    isOpen: isOpen && currentModalId === modalId,
    rootEl: rootEl,

    /**
     * `openModal` takes an optional `id` or `ref` that specifies which element to return focus to when `Modal` closes.
     * If nothing is passed in, focus returns to the element that was clicked to open the modal.
     */
    openModal: openModal,

    /**
     * `closeModal` can optionally specify which element to return focus to when `Modal` closes.
     * This is useful to manage the focus when a `Modal` contains both a destructive and cancel action.
     */
    closeModal: closeModal,
    dispatch: dispatch,
    ModalContextActions: ModalContextActions,
    multipleModalCache: multipleModalCache
  };
};

export { ModalContextProvider, useModal, useModalDispatch, useModalState, ModalContextActions };